import { AlertTriangle, EyeOff } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import { buildDayTimeline } from "../utils/overlap";
import { localDayKey } from "../../../lib/days";

function fmtTime(date) {
  if (!(date instanceof Date)) return "";
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

// Day-relative time text for a conflict-popover row. Multi-day / cross-midnight
// events are clipped to the day (matching the mobile agenda) so the popover
// never shows a raw multi-day range; an event that began earlier is marked
// "(continued)". A plain render helper (not a component) so this file keeps a
// single component-factory export for react-refresh.
function renderRowTime(row) {
  return (
    <span className="font-mono tabular-nums text-slate-500 dark:text-slate-400">
      {row.isAllDay
        ? "All day"
        : `${fmtTime(row.clippedStart)}–${fmtTime(row.clippedEnd)}`}
      {row.isContinuation && (
        <span className="ml-1" aria-hidden="true">
          (continued)
        </span>
      )}
    </span>
  );
}

/**
 * react-big-calendar passes { date, label } into components.month.dateHeader.
 *
 * We render:
 *   - a corner pip on the RIGHT for days in `conflictDays`
 *   - a "# hidden" pip on the LEFT for days with hidden events (only when
 *     the parent has decided to render them — i.e. `Show hidden events`
 *     is true; the parent passes an empty map otherwise)
 *
 * Both pips have a popover that lists the relevant events.
 */
export function makeMonthDateHeader({
  conflictDays,
  allEvents,
  hiddenByDay = new Map(),
  onSelectHidden,
  timeZone,
}) {
  return function MonthDateHeader({ date, label, drilldownView, onDrillDown }) {
    // LOCAL day-key (browser zone by default) so the pip lookup matches the
    // browser-local cell RBC renders AND the conflictDays / hiddenByDay sets,
    // which are bucketed on the same basis (PR-C unification).
    const key = localDayKey(date, timeZone);
    // Today highlight (P1-A1): Dashboard + Voting draw a brand-blue today-circle
    // via `.rbc-now .rbc-button-link` (calendar-styles.css), which Availability
    // does not import and whose custom header would not inherit. Render the same
    // marker here so "today" is visually consistent across all four surfaces.
    const isToday = key === localDayKey(new Date(), timeZone);
    const isConflict = conflictDays.has(key);
    const timeline = isConflict
      ? buildDayTimeline(date, allEvents, { timeZone })
      : [];
    const hiddenForDay = hiddenByDay.get(key) || [];
    const hiddenCount = hiddenForDay.length;

    const dateLabel = (
      <button
        type="button"
        aria-current={isToday ? "date" : undefined}
        onClick={(e) => {
          if (drilldownView && onDrillDown) {
            onDrillDown(e);
          }
        }}
        className={
          isToday
            ? "inline-flex h-7 w-7 items-center justify-center rounded-full bg-brand-primary text-white no-underline shadow-md shadow-brand-primary/20"
            : "text-inherit no-underline"
        }
      >
        {label}
      </button>
    );

    if (!isConflict && hiddenCount === 0) {
      return <span className="rbc-button-link">{dateLabel}</span>;
    }

    return (
      <span className="relative flex items-center justify-between gap-1 px-1">
        {hiddenCount > 0 ? (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={`${hiddenCount} hidden events on ${key}`}
                onMouseDown={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium leading-none text-slate-600 hover:bg-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
              >
                <EyeOff className="h-2.5 w-2.5" aria-hidden="true" />
                {hiddenCount}
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-80 space-y-2 text-xs">
              <p className="font-medium text-slate-700 dark:text-slate-200">
                Hidden on this day
              </p>
              <ul className="space-y-0.5">
                {hiddenForDay.map((evt) => (
                  <li
                    key={`${evt.id}-${evt.start?.toISOString?.() || ""}`}
                  >
                    <button
                      type="button"
                      onClick={() => onSelectHidden?.(evt)}
                      disabled={!onSelectHidden}
                      className="flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left outline-none transition-colors enabled:hover:bg-slate-100 enabled:dark:hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-default"
                    >
                      <span
                        aria-hidden="true"
                        className="mt-1 inline-block h-2 w-2 shrink-0 rounded-full opacity-40"
                        style={{ backgroundColor: pickEventDot(evt) }}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-slate-900 dark:text-slate-100">
                          {evt.title}
                        </span>
                        {!evt.allDay && (
                          <span className="block font-mono text-[10px] tabular-nums text-slate-500 dark:text-slate-400">
                            {fmtTime(evt.start)}–{fmtTime(evt.end)}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </PopoverContent>
          </Popover>
        ) : (
          <span aria-hidden="true" />
        )}
        <span className="flex items-center gap-1">
          {dateLabel}
          {isConflict && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label={`Conflicts on ${key}`}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-amber-100 text-amber-600 ring-1 ring-amber-300 hover:bg-amber-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:bg-amber-900/50 dark:text-amber-300"
                >
                  <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 space-y-2 text-xs">
                <p className="font-medium text-slate-700 dark:text-slate-200">
                  Conflicts on this day
                </p>
                <ul className="space-y-1">
                  {timeline.map((row) => {
                    const evt = row.event;
                    return (
                      <li
                        key={`${evt.id}-${row.dayKey}-${row.clippedStart.toISOString()}`}
                        className="flex items-center gap-2"
                      >
                        <span
                          aria-hidden="true"
                          className="inline-block h-2 w-2 rounded-full"
                          style={{
                            backgroundColor: pickEventDot(evt),
                            opacity: evt.resource?.hidden ? 0.4 : 1,
                          }}
                        />
                        {renderRowTime(row)}
                        <span className="truncate">{evt.title}</span>
                      </li>
                    );
                  })}
                </ul>
              </PopoverContent>
            </Popover>
          )}
        </span>
      </span>
    );
  };
}

function pickEventDot(evt) {
  const r = evt.resource || {};
  if (r.kind === "finalizedSession" || r.kind === "busyWindow") return "#0f766e";
  return r.color || "#94a3b8";
}
